import { type ReactNode, useEffect, useState } from 'react';
import type { CreateDatabaseResponse, Database } from '@tallypg/contracts';

export type Theme = 'dark' | 'light';
const THEME_KEY = 'tallypg_theme';

export function currentTheme(): Theme {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* storage blocked */
  }
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* storage blocked */
  }
}

/** Dark / light switch, fixed top-right on every page. index.html applies the saved theme before first paint. */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => currentTheme());
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      className="theme-toggle"
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
      onClick={() => {
        applyTheme(next);
        setTheme(next);
      }}
    >
      {theme === 'dark' ? (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      )}
    </button>
  );
}

/** Brand mark shared with tallypg.com (three tally strokes and a slash). */
export function Logo({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="6.5" fill="#41e69c" />
      <rect x="5.6" y="6" width="2.4" height="12" rx="1.2" fill="#0a0b0d" />
      <rect x="10.8" y="6" width="2.4" height="12" rx="1.2" fill="#0a0b0d" />
      <rect x="16" y="6" width="2.4" height="12" rx="1.2" fill="#0a0b0d" />
      <rect
        x="3.2"
        y="14.2"
        width="17.6"
        height="2.4"
        rx="1.2"
        fill="#0a0b0d"
        transform="rotate(-32 12 15.4)"
      />
    </svg>
  );
}

export function StatusBadge({ state }: { state: string }) {
  const label: Record<string, string> = {
    active: 'Available',
    provisioning: 'Creating',
    requested: 'Creating',
    payment_required: 'Payment required',
    deleting: 'Deleting',
    deleted: 'Deleted',
    failed: 'Failed',
    disabled: 'Disabled',
  };
  return <span className={`badge ${state}`}>{label[state] ?? state}</span>;
}

export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row between" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <button className="secondary" onClick={onClose}>
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="secondary"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}

/** Shown exactly once after creation or rotation. The control plane never stores this password. */
export function CredentialsReveal({ result }: { result: CreateDatabaseResponse }) {
  return (
    <div>
      <div className="banner">
        Save these credentials now. For your security we do not keep a copy; you can rotate the password later
        to get a new one.
      </div>
      <label>Connection string</label>
      <div className="row">
        <div className="secret" style={{ flex: 1 }}>
          {result.credentials.connectionString}
        </div>
        <CopyButton text={result.credentials.connectionString} />
      </div>
      <label style={{ marginTop: 12 }}>Password</label>
      <div className="row">
        <div className="secret" style={{ flex: 1 }}>
          {result.credentials.password}
        </div>
        <CopyButton text={result.credentials.password} />
      </div>
      <label style={{ marginTop: 12 }}>
        Pooled connection string (for serverless functions; transaction pooling)
      </label>
      <div className="row">
        <div className="secret" style={{ flex: 1 }}>
          {result.credentials.poolerConnectionString}
        </div>
        <CopyButton text={result.credentials.poolerConnectionString} />
      </div>
    </div>
  );
}

const TABS = ['psql', 'Node (pg)', 'Drizzle', 'Prisma', 'Python (psycopg)', 'Cloudflare Workers'] as const;

/** Connection examples with a placeholder password (the real one was shown once at creation). */
export function ConnectModal({ database, onClose }: { database: Database; onClose: () => void }) {
  const [tab, setTab] = useState<(typeof TABS)[number]>('psql');
  const c = database.connection;
  const url = `postgresql://${c.user}:PASSWORD@${c.host}:${c.port}/${c.database}?sslmode=verify-full`;
  const pooled = `postgresql://${c.user}:PASSWORD@${c.host}:${c.poolerPort}/${c.database}?sslmode=verify-full`;
  const examples: Record<(typeof TABS)[number], string> = {
    psql: `psql "${url}"`,
    'Node (pg)': `import pg from 'pg';

const pool = new pg.Pool({ connectionString: process.env.DATABASE_URL, ssl: true });
const { rows } = await pool.query('select now()');

// DATABASE_URL=${url}`,
    Drizzle: `import { drizzle } from 'drizzle-orm/node-postgres';

const db = drizzle(process.env.DATABASE_URL!);

// DATABASE_URL=${url}
// Serverless / edge runtimes: use the pooled URL on port ${c.poolerPort}`,
    Prisma: `// prisma/schema.prisma
datasource db {
  provider = "postgresql"
  url      = env("DATABASE_URL")
}

// .env
DATABASE_URL="${url}"
// Prisma with transaction pooling (pooled URL): add ?pgbouncer=true
// DATABASE_URL="${pooled}&pgbouncer=true"`,
    'Cloudflare Workers': `# 1. Point a Hyperdrive config at your database (pooling + caching at the edge)
npx wrangler hyperdrive create my-db --connection-string="${url}"

# 2. wrangler.jsonc
#   "hyperdrive": [{ "binding": "HYPERDRIVE", "id": "<id printed above>" }]

// 3. worker.ts (nodejs_compat flag required)
import { Client } from 'pg';
export default {
  async fetch(request, env) {
    const client = new Client({ connectionString: env.HYPERDRIVE.connectionString });
    await client.connect();
    const { rows } = await client.query('select now()');
    await client.end();
    return Response.json(rows);
  },
};`,
    'Python (psycopg)': `import psycopg

with psycopg.connect("${url}") as conn:
    with conn.cursor() as cur:
        cur.execute("select now()")
        print(cur.fetchone())`,
  };
  return (
    <Modal title={`Connect to ${database.name}`} onClose={onClose}>
      <p className="muted">
        Replace <code>PASSWORD</code> with the password you saved when the database was created. Lost it?
        Rotate the password to get a new one. Connections require TLS.
      </p>
      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={t === tab ? 'active' : ''} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>
      <pre>{examples[tab]}</pre>
      <div className="row" style={{ marginTop: 8 }}>
        <CopyButton text={examples[tab]} />
      </div>
      <h3 style={{ marginTop: 18, fontSize: 13 }}>Details</h3>
      <table>
        <tbody>
          <tr>
            <th>Host</th>
            <td>
              <code>{c.host}</code>
            </td>
          </tr>
          <tr>
            <th>Port</th>
            <td>
              <code>{c.port}</code> (sessions) · <code>{c.poolerPort}</code> (transaction pooling for
              serverless apps)
            </td>
          </tr>
          <tr>
            <th>User</th>
            <td>
              <code>{c.user}</code>
            </td>
          </tr>
          <tr>
            <th>Database</th>
            <td>
              <code>{c.database}</code>
            </td>
          </tr>
          <tr>
            <th>TLS</th>
            <td>Required (sslmode=require or verify-full)</td>
          </tr>
        </tbody>
      </table>
    </Modal>
  );
}
